'use client'

import * as React from 'react'

import { cn } from '@styleguide/lib/utils'

// Shared with `TokenField`, which is a contenteditable rather than a
// textarea but has to look and focus exactly like one.
const textareaVariants = (variant: 'default' | 'seamless' = 'default') =>
  cn(
    'border-components-input-border text-foreground placeholder:text-muted-foreground focus:border-components-input-active focus-visible:ring-components-input-focus aria-invalid:border-destructive focus:aria-invalid:border-destructive focus-visible:aria-invalid:ring-destructive-focus flex w-full rounded-md border bg-components-input-base px-3 py-2 text-base transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
    variant === 'seamless' && 'border-0 p-0 focus-visible:ring-0',
  )

// The focus treatment a seamless field gives up, drawn on the card that holds
// it instead. A ring on the bare field outlines the text inside the card, but
// the card is what reads as the field. Keyed on the field's own focus rather
// than `focus-within`, so a button in the card's footer does not light it.
const seamlessFieldHost = cn(
  'has-[[data-variant=seamless]:not([aria-invalid=true]):focus-visible]:border-components-input-active has-[[data-variant=seamless]:not([aria-invalid=true]):focus-visible]:ring-[3px] has-[[data-variant=seamless]:not([aria-invalid=true]):focus-visible]:ring-components-input-focus',
  'has-[[data-variant=seamless][aria-invalid=true]:focus-visible]:border-destructive has-[[data-variant=seamless][aria-invalid=true]:focus-visible]:ring-[3px] has-[[data-variant=seamless][aria-invalid=true]:focus-visible]:ring-destructive-focus',
)

interface TextareaProps extends React.ComponentProps<'textarea'> {
  /** Grow with the content instead of scrolling inside a fixed-height box. */
  autoGrow?: boolean
  /** With `autoGrow`, stop growing after this many rows and scroll instead. */
  maxRows?: number
  /**
   * `seamless` drops the border and padding so the field sits inside a card
   * as the card's own content. Its focus ring moves to that card, which takes
   * `seamlessFieldHost`: without one an editable draft looks like static
   * text, and keyboard users have nothing to follow (WCAG 2.4.7).
   */
  variant?: 'default' | 'seamless'
}

/**
 * Inside a card, use variant="seamless" and put seamlessFieldHost on the card so
 * focus still shows. The outreach compose card is the reference. Pass autoGrow
 * to grow with the text.
 *
 * @summary Use for free text that can run several lines, like a note or a reply.
 * For a message with merge tags or locked lines, use TokenField.
 */
function Textarea({
  className,
  autoGrow,
  maxRows,
  variant = 'default',
  ref,
  onInput,
  ...props
}: TextareaProps) {
  const innerRef = React.useRef<HTMLTextAreaElement | null>(null)

  const assignRef = React.useCallback(
    (node: HTMLTextAreaElement | null) => {
      innerRef.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    },
    [ref],
  )

  const resize = React.useCallback(() => {
    const el = innerRef.current
    if (!el || !autoGrow) return
    // scrollHeight never reports less than the current height, so the box has
    // to be collapsed before measuring or it could only ever grow.
    el.style.height = 'auto'
    // Unlaid-out environments (jsdom under test) report 0 and empty computed
    // styles; leave the height alone rather than collapsing the box to nothing.
    if (!el.scrollHeight) return
    const style = window.getComputedStyle(el)
    const px = (value: string, fallback = 0): number => {
      const parsed = parseFloat(value)
      return Number.isFinite(parsed) ? parsed : fallback
    }
    // Preflight sets border-box globally, so the height we assign has to cover
    // the borders that scrollHeight leaves out.
    const borderY = px(style.borderTopWidth) + px(style.borderBottomWidth)
    const natural = el.scrollHeight + borderY
    const cap = maxRows
      ? maxRows * px(style.lineHeight, 20) +
        px(style.paddingTop) +
        px(style.paddingBottom) +
        borderY
      : Number.POSITIVE_INFINITY
    el.style.height = `${Math.min(natural, cap)}px`
    el.style.overflowY = natural > cap ? 'auto' : 'hidden'
  }, [autoGrow, maxRows])

  // Covers mount and controlled updates — including a programmatic reset (the
  // composer clearing itself after a send), which fires no input event.
  React.useLayoutEffect(resize, [resize, props.value])

  return (
    <textarea
      data-slot="textarea"
      data-variant={variant}
      ref={assignRef}
      onInput={(event) => {
        // Uncontrolled callers have no `value` for the effect above to watch.
        resize()
        onInput?.(event)
      }}
      className={cn(
        textareaVariants(variant),
        autoGrow ? 'min-h-0 resize-none' : 'min-h-16',
        className,
      )}
      {...props}
    />
  )
}

export { Textarea, textareaVariants, seamlessFieldHost, type TextareaProps }
